"use client";

import React from "react";
import { motion } from "framer-motion";
import {
  Zap,
  MonitorSmartphone,
  Shield,
  Share2,
  Lock,
  Globe,
  Cpu,
  Hash,
  Activity
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/hooks/useTheme";
import { ThemeSection, ThemeText, AuroraBlobs } from "@/components/ui/theme-utils";

interface FeaturesProps {
  showTitle?: boolean;
}

export function Features({ showTitle = true }: FeaturesProps) {
  const { isDark } = useTheme();

  return (
    <ThemeSection id="features" pattern="dots">
      <AuroraBlobs variant="violet" />

      <div className="container mx-auto px-5 sm:px-6 relative z-10">
        {showTitle && (
          <motion.div
            className="text-center mb-16 sm:mb-20 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.6 }}
          >
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium mb-5",
                isDark
                  ? "bg-white/[0.04] border border-white/10 text-white/80"
                  : "bg-white border border-stone-200 text-stone-700"
              )}
            >
              <Activity className="h-3.5 w-3.5 text-brand-500" />
              Built for high-throughput teams
            </span>
            <h2 className="responsive-heading-2 mb-5">
              Everything you need to{" "}
              <span className="text-gradient">move massive files</span>
            </h2>
            <ThemeText variant="subtle" className="responsive-text-lg">
              From first byte to final delivery — Zset handles the messy parts of
              large-file transfers so your team can stay focused on the work.
            </ThemeText>
          </motion.div>
        )}

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 sm:gap-5">
          {/* Tile 1: Speed - large */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.5 }}
            className="md:col-span-4 md:row-span-2"
          >
            <BentoCard className="h-full p-6 sm:p-8" glow>
              <div className="flex flex-col h-full">
                <div className="flex items-center gap-3 mb-5">
                  <IconBadge>
                    <Zap className="h-5 w-5" />
                  </IconBadge>
                  <span
                    className={cn(
                      "text-xs font-medium px-2.5 py-1 rounded-full",
                      isDark ? "bg-white/5 text-white/70" : "bg-stone-100 text-stone-700"
                    )}
                  >
                    Speed
                  </span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">
                  Max bandwidth, every transfer
                </h3>
                <ThemeText variant="subtle" className="max-w-md">
                  Parallel multi-part uploads saturate your connection. 1.2 GB
                  delivery in seconds, not minutes.
                </ThemeText>
              </div>
            </BentoCard>
          </motion.div>

          {/* Tile 2: Auto-retry - tall */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="md:col-span-2 md:row-span-2"
          >
            <BentoCard className="h-full p-6 sm:p-8" glow>
              <div className="flex items-center gap-3 mb-5">
                <IconBadge>
                  <MonitorSmartphone className="h-5 w-5" />
                </IconBadge>
                <span
                  className={cn(
                    "text-xs font-medium px-2.5 py-1 rounded-full",
                    isDark ? "bg-white/5 text-white/70" : "bg-stone-100 text-stone-700"
                  )}
                >
                  Resilience
                </span>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold tracking-tight mb-2">
                Set it & forget it
              </h3>
              <ThemeText variant="subtle" className="text-sm">
                Dropped connection, server hiccup, laptop sleep — Zset recovers
                silently and keeps going.
              </ThemeText>

              <div className="mt-5 grid grid-cols-2 gap-3 text-center">
                <Stat n="24/7" label="Watchdog" />
                <Stat n="0" label="Manual retries" />
              </div>
            </BentoCard>
          </motion.div>

          {/* Tile 3: Encryption */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="md:col-span-3"
          >
            <BentoCard className="h-full p-6 sm:p-7" glow>
              <div className="flex items-start gap-4">
                <IconBadge>
                  <Lock className="h-5 w-5" />
                </IconBadge>
                <div className="flex-1">
                  <h3 className="text-lg sm:text-xl font-bold tracking-tight mb-1.5">
                    End-to-end encryption, packet by packet
                  </h3>
                  <ThemeText variant="subtle" className="text-sm">
                    Files are split into encrypted packets in transit. No full
                    file ever leaves your device unprotected.
                  </ThemeText>
                </div>
              </div>
            </BentoCard>
          </motion.div>

          {/* Tile 4: Permissions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="md:col-span-3"
          >
            <BentoCard className="h-full p-6 sm:p-7" glow>
              <div className="flex items-start gap-4">
                <IconBadge>
                  <Share2 className="h-5 w-5" />
                </IconBadge>
                <div className="flex-1">
                  <h3 className="text-lg sm:text-xl font-bold tracking-tight mb-1.5">
                    Share with surgical precision
                  </h3>
                  <ThemeText variant="subtle" className="text-sm">
                    Expiring links, password locks, view-only — give exactly the
                    access you intend, nothing more.
                  </ThemeText>
                </div>
              </div>
            </BentoCard>
          </motion.div>

          {/* Tile 5: Hash / Integrity */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="md:col-span-2"
          >
            <BentoCard className="h-full p-6" glow>
              <IconBadge className="mb-4">
                <Hash className="h-5 w-5" />
              </IconBadge>
              <h3 className="text-lg font-bold tracking-tight mb-1">
                HASH-validated
              </h3>
              <ThemeText variant="subtle" className="text-sm">
                Checksums catch corruption before delivery.
              </ThemeText>
            </BentoCard>
          </motion.div>

          {/* Tile 6: Global */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="md:col-span-2"
          >
            <BentoCard className="h-full p-6" glow>
              <IconBadge className="mb-4">
                <Globe className="h-5 w-5" />
              </IconBadge>
              <h3 className="text-lg font-bold tracking-tight mb-1">
                Global edge
              </h3>
              <ThemeText variant="subtle" className="text-sm">
                14 PoPs route you the fastest path.
              </ThemeText>
            </BentoCard>
          </motion.div>

          {/* Tile 7: Smart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="md:col-span-2"
          >
            <BentoCard className="h-full p-6" glow>
              <IconBadge className="mb-4">
                <Cpu className="h-5 w-5" />
              </IconBadge>
              <h3 className="text-lg font-bold tracking-tight mb-1">
                Adaptive
              </h3>
              <ThemeText variant="subtle" className="text-sm">
                Re-tunes to your network in real time.
              </ThemeText>
            </BentoCard>
          </motion.div>
        </div>
      </div>
    </ThemeSection>
  );
}

/* ---------- internal ---------- */

function BentoCard({
  children,
  className,
  glow = false
}: {
  children: React.ReactNode;
  className?: string;
  glow?: boolean;
}) {
  const { isDark } = useTheme();
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-3xl transition-all duration-300 group",
        isDark
          ? "bg-white/[0.025] border border-white/[0.08] hover:border-white/15"
          : "bg-white/70 border border-stone-200/80 hover:border-stone-300/80",
        glow && isDark && "hover:shadow-glow",
        glow && !isDark && "hover:shadow-elevated",
        className
      )}
    >
      <div
        className={cn(
          "pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700",
          "bg-gradient-to-br from-brand-700/30 to-brand-900/20 blur-2xl"
        )}
      />
      <div className="relative">{children}</div>
    </div>
  );
}

function IconBadge({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const { isDark } = useTheme();
  return (
    <span
      className={cn(
        "inline-flex h-10 w-10 items-center justify-center rounded-xl",
        "bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-glow-sm",
        "ring-1 ring-inset ring-white/10",
        className
      )}
    >
      {children}
    </span>
  );
}

function Stat({ n, label }: { n: string; label: string }) {
  const { isDark } = useTheme();
  return (
    <div
      className={cn(
        "rounded-xl py-2.5 px-2",
        isDark ? "bg-white/[0.03]" : "bg-stone-100/80"
      )}
    >
      <div className="text-base sm:text-lg font-bold tracking-tight">{n}</div>
      <div className="text-[10px] uppercase tracking-wider text-stone-500">
        {label}
      </div>
    </div>
  );
}
